import { useState } from "react";
import { StaffLayout } from "@/components/StaffLayout";
import { useDocumentTitle } from "@/lib/use-document-title";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, AlertCircle, Loader2, ScanLine } from "lucide-react";

type RedeemResult = {
  log_id: string;
  member_id: string;
  member_number: string;
  member_name: string;
  benefit_label: string;
};

function LosaInPage() {
  useDocumentTitle("Lös in förmånskod — ÅF-Portal");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RedeemResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  function onChange(v: string) {
    const digits = v.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    setError(null);
    setResult(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6) {
      setError("Ange 6 siffror");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    const { data, error: rpcError } = await supabase.rpc("redeem_benefit_code" as any, { _code: code });
    setLoading(false);
    if (rpcError) {
      setError(rpcError.message || "Något gick fel");
      return;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      setError("Ogiltig eller utgången kod");
      return;
    }
    setResult(row as RedeemResult);
    setCode("");
  }

  return (
    <StaffLayout title="Lös in förmånskod">
      <div className="max-w-md">
        <p className="text-sm text-muted-foreground mb-6">
          Kunden visar en 6-siffrig kod i sin app. Knappa in den nedan för att bekräfta förmånen.
        </p>

        <form onSubmit={submit} className="card-soft p-6 space-y-4">
          <label htmlFor="benefit-code" className="block text-xs uppercase tracking-widest text-muted-foreground font-display">
            Förmånskod
          </label>
          <input
            id="benefit-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            autoComplete="off"
            value={code}
            onChange={(e) => onChange(e.target.value)}
            placeholder="000000"
            className="w-full font-mono text-center text-4xl tracking-[0.4em] tabular-nums bg-muted rounded-xl py-5 focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Sex siffrig förmånskod"
          />
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="btn-pill w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="inline animate-spin mr-2" size={16} aria-hidden="true" />
                Löser in…
              </>
            ) : (
              <>
                <ScanLine className="inline mr-2 -mt-0.5" size={16} aria-hidden="true" />
                Bekräfta inlösen
              </>
            )}
          </button>
        </form>

        {error && (
          <div className="mt-5 flex items-start gap-3 rounded-lg bg-destructive/5 border border-destructive/20 p-4">
            <AlertCircle className="text-destructive shrink-0 mt-0.5" size={18} aria-hidden="true" />
            <div className="text-sm">
              <p className="font-display font-semibold text-destructive mb-0.5">Ogiltig kod</p>
              <p className="text-muted-foreground">{error}</p>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-5 rounded-xl bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 p-5">
            <div className="flex items-center gap-2 text-[var(--color-success)] font-display font-semibold mb-3">
              <CheckCircle2 size={18} aria-hidden="true" /> Förmån inlöst
            </div>
            <div className="space-y-1.5 text-sm">
              <Row label="Kund" value={result.member_name} />
              <Row label="Medlemsnr" value={result.member_number} mono />
              <Row label="Förmån" value={result.benefit_label} />
            </div>
          </div>
        )}
      </div>
    </StaffLayout>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-display font-semibold text-right ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

export default LosaInPage;
